import { useState, useCallback, useEffect, useRef } from 'react';

const STORAGE_PREFIX = 'nexo_filters_';

/**
 * useState-like hook that persists filter/selection state to localStorage.
 * Survives browser restarts — filters are restored when the user returns to the view.
 *
 * @param viewName Unique identifier for the view (e.g. 'fieldRestrictions', 'dynamicFields')
 * @param defaultFilters Default filter values when nothing is stored
 */
export function usePersistedFilters<T>(viewName: string, defaultFilters: T) {
  const storageKey = `${STORAGE_PREFIX}${viewName}`;
  const isInitialized = useRef(false);

  const [filters, setFiltersRaw] = useState<T>(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) return JSON.parse(stored) as T;
    } catch {
      // Ignore parse errors
    }
    return defaultFilters;
  });

  // Persist to localStorage whenever filters change (skip initial render)
  useEffect(() => {
    if (!isInitialized.current) {
      isInitialized.current = true;
      return;
    }
    try {
      localStorage.setItem(storageKey, JSON.stringify(filters));
    } catch {
      // Ignore storage errors
    }
  }, [filters, storageKey]);

  const setFilters = useCallback((updater: T | ((prev: T) => T)) => {
    setFiltersRaw(updater);
  }, []);

  return [filters, setFilters] as const;
}
